import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Mail, Search, Menu, X } from 'lucide-react';
import { mainNav } from '../../data/navigation';
import { companyInfo } from '../../data/company';
import BetaLink from '../../beta/BetaLink';
import { useBetaProgram } from '../../beta/hooks';
import { stripBetaPrefix } from '../../beta/routing';

const mergeNavItems = (baseItems, extraItems) => {
  const seen = new Set();
  return [...baseItems, ...extraItems].filter((item) => {
    if (seen.has(item.href)) {
      return false;
    }

    seen.add(item.href);
    return true;
  });
};

const getProgramAdjustedMainNav = (program) => {
  if (program?.slug !== 'bidor-style-catalog') {
    return mainNav;
  }

  const pastryIndex = mainNav.findIndex((item) => item.href === '/pastry-mixes');
  const prunedNav = mainNav.filter(
    (item) => item.href !== '/pastry-mixes' && item.href !== '/bakery-mixes'
  );
  const insertIndex = pastryIndex >= 0 ? pastryIndex : 2;

  return [
    ...prunedNav.slice(0, insertIndex),
    { label: 'MIX CATALOG', href: '/mixes-catalog' },
    ...prunedNav.slice(insertIndex)
  ];
};

const Header = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { program, withProgramPath } = useBetaProgram();
  const adjustedMainNav = getProgramAdjustedMainNav(program);
  const navItems = mergeNavItems(adjustedMainNav, program?.extraNav || []);
  const currentPath = stripBetaPrefix(location.pathname);

  const isActiveNavItem = (href) => {
    if (href === '/') {
      return currentPath === '/';
    }

    return currentPath === href;
  };

  return (
    <header className="w-full">
      {/* Top bar with logo and email */}
      <div className="bg-white py-4 px-6 flex items-center justify-between max-w-7xl mx-auto">
        <BetaLink to={withProgramPath('/')} className="flex items-center">
          <img
            src="/images/branding/mirage-logo.jpg"
            alt="Mirage Fine Desserts"
            className="h-16 w-auto"
          />
        </BetaLink>

        <a
          href={`mailto:${companyInfo.email}`}
          className="flex items-center gap-2 text-primary-red hover:text-primary-red-dark transition-colors"
        >
          <Mail size={20} />
          <span className="hidden sm:inline">{companyInfo.email}</span>
        </a>
      </div>

      {/* Navigation bar */}
      <nav className="bg-primary-red">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Desktop Navigation */}
            <ul className="hidden lg:flex">
              {navItems.map((item) => (
                <li key={item.href}>
                  <BetaLink
                    to={withProgramPath(item.href)}
                    className={`block px-4 py-4 text-white text-sm font-medium tracking-wide hover:bg-primary-red-dark transition-colors ${
                      isActiveNavItem(item.href) ? 'bg-primary-red-dark' : ''
                    }`}
                  >
                    {item.label}
                  </BetaLink>
                </li>
              ))}
            </ul>

            {/* Mobile menu button */}
            <button
              className="lg:hidden p-4 text-white"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>

            {/* Search button */}
            <button
              className="p-4 text-white hover:bg-primary-red-dark transition-colors"
              onClick={() => setIsSearchOpen(!isSearchOpen)}
            >
              <Search size={20} />
            </button>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <ul className="lg:hidden pb-4">
              {navItems.map((item) => (
                <li key={item.href}>
                  <BetaLink
                    to={withProgramPath(item.href)}
                    className={`block px-4 py-3 text-white text-sm font-medium hover:bg-primary-red-dark transition-colors ${
                      isActiveNavItem(item.href) ? 'bg-primary-red-dark' : ''
                    }`}
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item.label}
                  </BetaLink>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Search bar */}
        {isSearchOpen && (
          <div className="bg-white p-4 border-t">
            <form className="max-w-md mx-auto flex gap-2">
              <input
                type="text"
                placeholder="Search Here ..."
                className="flex-1 px-4 py-2 border border-gray-300 rounded focus:outline-none focus:border-primary-red"
              />
              <button
                type="submit"
                className="px-6 py-2 bg-primary-red text-white rounded hover:bg-primary-red-dark transition-colors"
              >
                Search
              </button>
            </form>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
